'use client';

import { useState, useEffect } from 'react';
import { useTranslations } from 'next-intl';

export default function BoatLoanCalculator() {
  const t = useTranslations("tools.boatLoanCalculator");
  const [screenSize, setScreenSize] = useState('mobile');
  const [currentTab, setCurrentTab] = useState<'standard' | 'reverse'>('standard');
  const [showResults, setShowResults] = useState(false);
  const [showAmortization, setShowAmortization] = useState(false);

  const [inputs, setInputs] = useState({
    boatPrice: 35000,
    monthlyPaymentInput: 350,
    loanTerm: 10,
    loanTermUnit: 'years' as 'years' | 'months',
    interestRate: 7,
    downPayment: 7000,
    downPaymentUnit: 'dollar' as 'dollar' | 'percent',
    tradeIn: 0,
    salesTax: 7,
    fees: 2000,
    includeFees: true,
  });

  const [results, setResults] = useState({
    monthlyPayment: 0,
    totalLoan: 0,
    salesTaxAmount: 0,
    upfrontPayment: 0,
    totalPayments: 0,
    totalInterest: 0,
    totalCost: 0,
    principal: 0,
    interest: 0,
  });

  const [amortizationSchedule, setAmortizationSchedule] = useState<Array<{
    month: number;
    payment: number;
    principal: number;
    interest: number;
    balance: number;
  }>>([]);

  useEffect(() => {
    const updateScreenSize = () => {
      const width = window.innerWidth;
      if (width >= 1024) {
        setScreenSize('desktop');
      } else if (width >= 768) {
        setScreenSize('tablet');
      } else {
        setScreenSize('mobile');
      }
    };

    updateScreenSize();
    window.addEventListener('resize', updateScreenSize);
    return () => window.removeEventListener('resize', updateScreenSize);
  }, []);

  useEffect(() => {
    handleCalculate();
  }, []);

  const getResponsiveStyle = (baseStyle: any, mobileOverrides?: any, tabletOverrides?: any, desktopOverrides?: any) => {
    let style = { ...baseStyle };

    if (screenSize === 'mobile' && mobileOverrides) {
      style = { ...style, ...mobileOverrides };
    } else if (screenSize === 'tablet' && tabletOverrides) {
      style = { ...style, ...tabletOverrides };
    } else if (screenSize === 'desktop' && desktopOverrides) {
      style = { ...style, ...desktopOverrides };
    }

    return style;
  };

  const colors = {
    primary: '#ff5a70',
    primaryHover: '#f63b3b',
    secondary: '#ffc400',
    accent: '#ff6b95',
    textPrimary: '#2d3748',
    textSecondary: '#4a5568',
    glassBg: 'rgba(255, 255, 255, 0.6)',
    glassBorder: 'rgba(255, 200, 180, 0.2)',
    blue: '#2563eb',
    blueHover: '#1d4ed8',
    green: '#8bbc21',
    lightBg: '#f8fafc',
    border: '#e2e8f0',
  };

  const handleCalculate = () => {
    if (currentTab === 'standard') {
      calculateStandard();
    } else {
      calculateReverse();
    }
    setShowResults(true);
    setShowAmortization(true);
  };

  const calculateStandard = () => {
    const boatPrice = inputs.boatPrice;
    let loanTerm = inputs.loanTerm;
    const loanTermUnit = inputs.loanTermUnit;
    const interestRate = inputs.interestRate;
    let downPayment = inputs.downPayment;
    const downPaymentUnitValue = inputs.downPaymentUnit;
    const tradeIn = inputs.tradeIn;
    const salesTaxRate = inputs.salesTax;
    const fees = inputs.fees;
    const includeFees = inputs.includeFees;

    if (loanTermUnit === 'years') {
      loanTerm = loanTerm * 12;
    }

    if (downPaymentUnitValue === 'percent') {
      downPayment = (boatPrice * downPayment) / 100;
    }

    const salesTax = (boatPrice * salesTaxRate) / 100;

    let loanAmount = boatPrice - downPayment - tradeIn;

    if (includeFees) {
      loanAmount += fees;
    }

    const monthlyRate = interestRate / 100 / 12;
    let monthlyPayment = 0;

    if (monthlyRate === 0) {
      monthlyPayment = loanAmount / loanTerm;
    } else {
      monthlyPayment = loanAmount * (monthlyRate * Math.pow(1 + monthlyRate, loanTerm)) / (Math.pow(1 + monthlyRate, loanTerm) - 1);
    }

    const totalPayments = monthlyPayment * loanTerm;
    const totalInterest = totalPayments - loanAmount;

    let upfrontPayment = downPayment + salesTax;
    if (!includeFees) {
      upfrontPayment += fees;
    }
    upfrontPayment -= tradeIn;

    const totalCost = boatPrice + totalInterest + salesTax + fees;

    setResults({
      monthlyPayment,
      totalLoan: loanAmount,
      salesTaxAmount: salesTax,
      upfrontPayment,
      totalPayments,
      totalInterest,
      totalCost,
      principal: loanAmount,
      interest: totalInterest,
    });

    generateAmortization(loanAmount, monthlyRate, monthlyPayment, loanTerm);
  };

  const calculateReverse = () => {
    const monthlyPaymentInput = inputs.monthlyPaymentInput;
    let loanTerm = inputs.loanTerm;
    const loanTermUnit = inputs.loanTermUnit;
    const interestRate = inputs.interestRate;
    let downPayment = inputs.downPayment;
    const downPaymentUnitValue = inputs.downPaymentUnit;
    const tradeIn = inputs.tradeIn;
    const salesTaxRate = inputs.salesTax;
    const fees = inputs.fees;
    const includeFees = inputs.includeFees;

    if (loanTermUnit === 'years') {
      loanTerm = loanTerm * 12;
    }

    const monthlyRate = interestRate / 100 / 12;
    let loanAmount = 0;

    if (monthlyRate === 0) {
      loanAmount = monthlyPaymentInput * loanTerm;
    } else {
      loanAmount = monthlyPaymentInput * (Math.pow(1 + monthlyRate, loanTerm) - 1) / (monthlyRate * Math.pow(1 + monthlyRate, loanTerm));
    }

    let boatPrice = loanAmount + tradeIn;

    if (includeFees) {
      boatPrice -= fees;
    }

    if (downPaymentUnitValue === 'percent') {
      boatPrice = boatPrice / (1 - downPayment / 100);
      downPayment = (boatPrice * downPayment) / 100;
    } else {
      boatPrice += downPayment;
    }

    const salesTax = (boatPrice * salesTaxRate) / 100;
    const totalPayments = monthlyPaymentInput * loanTerm;
    const totalInterest = totalPayments - loanAmount;

    let upfrontPayment = downPayment + salesTax;
    if (!includeFees) {
      upfrontPayment += fees;
    }
    upfrontPayment -= tradeIn;

    const totalCost = boatPrice + totalInterest + salesTax + fees;

    setResults({
      monthlyPayment: monthlyPaymentInput,
      totalLoan: loanAmount,
      salesTaxAmount: salesTax,
      upfrontPayment,
      totalPayments,
      totalInterest,
      totalCost,
      principal: loanAmount,
      interest: totalInterest,
    });

    generateAmortization(loanAmount, monthlyRate, monthlyPaymentInput, loanTerm);
  };

  const generateAmortization = (principal: number, monthlyRate: number, monthlyPayment: number, totalMonths: number) => {
    const schedule = [];
    let balance = principal;
    const displayMonths = Math.min(12, totalMonths);

    for (let month = 1; month <= displayMonths; month++) {
      const interestPayment = balance * monthlyRate;
      const principalPayment = monthlyPayment - interestPayment;
      balance -= principalPayment;

      schedule.push({
        month,
        payment: monthlyPayment,
        principal: principalPayment,
        interest: interestPayment,
        balance: Math.max(0, balance),
      });
    }

    setAmortizationSchedule(schedule);
  };

  const handleClear = () => {
    setInputs({
      boatPrice: 35000,
      monthlyPaymentInput: 350,
      loanTerm: 10,
      loanTermUnit: 'years',
      interestRate: 7,
      downPayment: 7000,
      downPaymentUnit: 'dollar',
      tradeIn: 0,
      salesTax: 7,
      fees: 2000,
      includeFees: true,
    });
    setShowResults(false);
    setShowAmortization(false);
  };

  const getPieChartPath = (startAngle: number, endAngle: number) => {
    const cx = 100;
    const cy = 110;
    const radius = 45;

    const start = polarToCartesian(cx, cy, radius, endAngle);
    const end = polarToCartesian(cx, cy, radius, startAngle);
    const largeArcFlag = endAngle - startAngle <= 180 ? '0' : '1';

    return `M ${cx} ${cy} L ${start.x} ${start.y} A ${radius} ${radius} 0 ${largeArcFlag} 0 ${end.x} ${end.y} Z`;
  };

  const polarToCartesian = (centerX: number, centerY: number, radius: number, angleInDegrees: number) => {
    const angleInRadians = (angleInDegrees - 90) * Math.PI / 180.0;
    return {
      x: centerX + (radius * Math.cos(angleInRadians)),
      y: centerY + (radius * Math.sin(angleInRadians)),
    };
  };

  const containerStyle = getResponsiveStyle(
    { maxWidth: '1200px', margin: '0 auto', position: 'relative' as const, zIndex: 1 },
    { padding: '1rem' },
    { padding: '1.5rem' },
    { padding: '2rem' }
  );

  const headerStyle = getResponsiveStyle(
    { textAlign: 'center' as const, marginBottom: '2rem' },
    { marginBottom: '1.5rem' },
    { marginBottom: '2rem' },
    { marginBottom: '2.5rem' }
  );

  const h1Style = getResponsiveStyle(
    { fontWeight: 700, color: colors.textPrimary, marginBottom: '0.75rem', lineHeight: 1.2 },
    { fontSize: '1.75rem' },
    { fontSize: '2.25rem' },
    { fontSize: '2.5rem' }
  );

  const subtitleStyle = getResponsiveStyle(
    { color: colors.textSecondary, lineHeight: 1.6 },
    { fontSize: '1rem' },
    { fontSize: '1.125rem' },
    { fontSize: '1.25rem' }
  );

  const calculatorToolStyle = getResponsiveStyle(
    {
      background: 'linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)',
      borderRadius: '1rem',
      marginBottom: '2rem',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Oxygen, Ubuntu, Cantarell, sans-serif',
    },
    { padding: '1.5rem 1rem' },
    { padding: '2rem 1.5rem' },
    { padding: '3rem 1.5rem' }
  );

  const toolContainerStyle = getResponsiveStyle(
    { maxWidth: '1200px', margin: '0 auto' },
    {},
    {},
    {}
  );

  const toolHeaderStyle = getResponsiveStyle(
    { textAlign: 'center' as const, marginBottom: '1.5rem' },
    {},
    { marginBottom: '2rem' },
    { marginBottom: '2.5rem' }
  );

  const toolH2Style = getResponsiveStyle(
    { fontWeight: 700, color: '#1e293b', marginBottom: '0.5rem' },
    { fontSize: '1.5rem' },
    { fontSize: '1.75rem' },
    { fontSize: '2rem' }
  );

  const calculatorLayoutStyle = getResponsiveStyle(
    { display: 'grid', gap: '2rem', marginBottom: '3rem' },
    { gridTemplateColumns: '1fr' },
    { gridTemplateColumns: '1fr' },
    { gridTemplateColumns: '1fr 1fr' }
  );

  const calculatorFormStyle = getResponsiveStyle(
    {
      background: 'white',
      borderRadius: '1rem',
      boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
    },
    { padding: '1.5rem' },
    { padding: '2rem' },
    { padding: '2rem' }
  );

  const formTabsStyle = getResponsiveStyle(
    { display: 'flex', marginBottom: '1.5rem', borderBottom: '2px solid #e2e8f0' },
    { flexDirection: 'column' as const, gap: '0' },
    { flexDirection: 'row' as const, gap: '0.5rem' },
    { flexDirection: 'row' as const, gap: '0.5rem' }
  );

  const tabBtnStyle = (isActive: boolean) => getResponsiveStyle(
    {
      padding: '0.75rem 1.5rem',
      background: 'none',
      border: 'none',
      color: isActive ? colors.blue : '#64748b',
      fontWeight: 600,
      cursor: 'pointer',
      transition: 'all 0.3s',
      fontSize: '0.95rem',
    },
    {
      textAlign: 'left' as const,
      borderLeft: isActive ? `3px solid ${colors.blue}` : '3px solid transparent',
      borderBottom: 'none',
      paddingLeft: '1rem',
    },
    {
      borderBottom: isActive ? `3px solid ${colors.blue}` : '3px solid transparent',
      borderLeft: 'none',
    },
    {
      borderBottom: isActive ? `3px solid ${colors.blue}` : '3px solid transparent',
      borderLeft: 'none',
    }
  );

  const formGroupStyle = { marginBottom: '1.5rem' };

  const labelStyle = {
    display: 'block',
    marginBottom: '0.5rem',
    color: '#334155',
    fontWeight: 600,
    fontSize: '0.95rem',
  };

  const inputWrapperStyle = {
    position: 'relative' as const,
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
  };

  const inputStyle = getResponsiveStyle(
    {
      flex: 1,
      border: '2px solid #e2e8f0',
      borderRadius: '0.5rem',
      transition: 'all 0.3s',
      background: 'white',
    },
    { padding: '0.75rem 1rem', fontSize: '1rem' },
    { padding: '0.875rem 1.125rem', fontSize: '1rem' },
    { padding: '0.75rem 1rem', fontSize: '1rem' }
  );

  const inputPrefixStyle = {
    position: 'absolute' as const,
    left: '1rem',
    color: '#64748b',
    fontWeight: 600,
    pointerEvents: 'none' as const,
  };

  const inputSuffixStyle = {
    position: 'absolute' as const,
    right: '1rem',
    color: '#64748b',
    fontWeight: 600,
    pointerEvents: 'none' as const,
  };

  const selectStyle = getResponsiveStyle(
    {
      padding: '0.75rem 1rem',
      border: '2px solid #e2e8f0',
      borderRadius: '0.5rem',
      fontSize: '1rem',
      transition: 'all 0.3s',
      background: 'white',
      cursor: 'pointer',
    },
    {},
    {},
    {}
  );

  const checkboxGroupStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    marginTop: '1rem',
  };

  const checkboxStyle = {
    width: '1.25rem',
    height: '1.25rem',
    cursor: 'pointer',
  };

  const checkboxLabelStyle = {
    margin: 0,
    cursor: 'pointer',
    fontWeight: 500,
  };

  const buttonGroupStyle = getResponsiveStyle(
    { display: 'flex', gap: '1rem', marginTop: '2rem' },
    { flexDirection: 'column' as const },
    { flexDirection: 'row' as const },
    { flexDirection: 'row' as const }
  );

  const calculateBtnStyle = getResponsiveStyle(
    {
      flex: 1,
      border: 'none',
      borderRadius: '0.5rem',
      fontWeight: 600,
      cursor: 'pointer',
      transition: 'all 0.3s',
      background: `linear-gradient(135deg, ${colors.blue} 0%, #7c3aed 100%)`,
      color: 'white',
    },
    { padding: '1rem', fontSize: '1rem' },
    { padding: '1rem', fontSize: '1rem' },
    { padding: '1rem', fontSize: '1rem' }
  );

  const clearBtnStyle = getResponsiveStyle(
    {
      flex: 1,
      border: 'none',
      borderRadius: '0.5rem',
      fontWeight: 600,
      cursor: 'pointer',
      transition: 'all 0.3s',
      background: '#f1f5f9',
      color: '#475569',
    },
    { padding: '1rem', fontSize: '1rem' },
    { padding: '1rem', fontSize: '1rem' },
    { padding: '1rem', fontSize: '1rem' }
  );

  const resultsStyle = getResponsiveStyle(
    {
      background: 'white',
      borderRadius: '1rem',
      boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
    },
    { padding: '1.5rem' },
    { padding: '2rem' },
    { padding: '2rem' }
  );

  const resultsH3Style = getResponsiveStyle(
    { fontWeight: 700, color: '#1e293b', marginBottom: '1.5rem' },
    { fontSize: '1.25rem' },
    { fontSize: '1.5rem' },
    { fontSize: '1.5rem' }
  );

  const monthlyPaymentCardStyle = getResponsiveStyle(
    {
      background: `linear-gradient(135deg, ${colors.blue} 0%, #7c3aed 100%)`,
      color: 'white',
      borderRadius: '0.75rem',
      marginBottom: '1.5rem',
      textAlign: 'center' as const,
    },
    { padding: '1.25rem' },
    { padding: '1.5rem' },
    { padding: '1.5rem' }
  );

  const monthlyPaymentLabelStyle = {
    fontSize: '0.9rem',
    opacity: 0.9,
    marginBottom: '0.5rem',
  };

  const monthlyPaymentValueStyle = getResponsiveStyle(
    { fontWeight: 700 },
    { fontSize: '2rem' },
    { fontSize: '2.5rem' },
    { fontSize: '2.5rem' }
  );

  const resultItemStyle = {
    display: 'flex',
    justifyContent: 'space-between',
    padding: '1rem 0',
    borderBottom: '1px solid #e2e8f0',
  };

  const resultLabelStyle = {
    color: '#64748b',
    fontWeight: 500,
  };

  const resultValueStyle = getResponsiveStyle(
    { color: '#1e293b', fontWeight: 700 },
    { fontSize: '1rem' },
    { fontSize: '1.1rem' },
    { fontSize: '1.1rem' }
  );

  const resultHighlightStyle = {
    background: '#fef3c7',
    padding: '1rem',
    borderRadius: '0.5rem',
    borderLeft: '4px solid #f59e0b',
  };

  const chartContainerStyle = {
    margin: '2rem 0',
    padding: '1.5rem',
    background: '#f8fafc',
    borderRadius: '0.75rem',
  };

  const chartLegendStyle = {
    display: 'flex',
    justifyContent: 'center',
    gap: '2rem',
    marginTop: '1rem',
    flexWrap: 'wrap' as const,
  };

  const legendItemStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
  };

  const legendColorStyle = {
    width: '1rem',
    height: '1rem',
    borderRadius: '0.25rem',
  };

  const amortizationSectionStyle = getResponsiveStyle(
    {
      background: 'white',
      borderRadius: '1rem',
      boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
      marginTop: '2rem',
    },
    { padding: '1.5rem' },
    { padding: '2rem' },
    { padding: '2rem' }
  );

  const tableStyle = {
    width: '100%',
    borderCollapse: 'collapse' as const,
    overflowX: 'auto' as const,
    display: 'block',
  };

  const thStyle = getResponsiveStyle(
    {
      padding: '0.75rem',
      textAlign: 'left' as const,
      borderBottom: '1px solid #e2e8f0',
      background: '#f8fafc',
      fontWeight: 600,
      color: '#475569',
    },
    { fontSize: '0.85rem' },
    { fontSize: '0.9rem' },
    { fontSize: '0.9rem' }
  );

  const tdStyle = getResponsiveStyle(
    {
      padding: '0.75rem',
      textAlign: 'left' as const,
      borderBottom: '1px solid #e2e8f0',
      color: '#1e293b',
    },
    { fontSize: '0.85rem' },
    { fontSize: '0.9rem' },
    { fontSize: '0.9rem' }
  );

  const glassCardStyle = getResponsiveStyle(
    {
      background: colors.glassBg,
      backdropFilter: 'blur(20px) saturate(180%)',
      WebkitBackdropFilter: 'blur(20px) saturate(180%)',
      border: `1px solid ${colors.glassBorder}`,
      borderRadius: '1rem',
      boxShadow: '0 8px 32px 0 rgba(255, 90, 112, 0.08)',
      marginBottom: '2rem',
    },
    { padding: '1.5rem' },
    { padding: '2rem' },
    { padding: '2.5rem' }
  );

  const h2Style = getResponsiveStyle(
    { fontWeight: 600, color: colors.textPrimary, marginBottom: '1rem' },
    { fontSize: '1.5rem' },
    { fontSize: '1.75rem' },
    { fontSize: '2rem' }
  );

  const paragraphStyle = getResponsiveStyle(
    { lineHeight: 1.7, color: colors.textSecondary, marginBottom: '1rem' },
    { fontSize: '1rem' },
    { fontSize: '1.125rem' },
    { fontSize: '1.125rem' }
  );

  const listStyle = {
    paddingLeft: '1.5rem',
    marginBottom: '1rem',
  };

  const listItemStyle = getResponsiveStyle(
    { marginBottom: '0.75rem', lineHeight: 1.6, color: colors.textSecondary },
    { fontSize: '1rem' },
    { fontSize: '1.125rem' },
    { fontSize: '1.125rem' }
  );

  const total = results.principal + results.interest;
  const principalPercent = total > 0 ? ((results.principal / total) * 100).toFixed(0) : '0';
  const interestPercent = total > 0 ? ((results.interest / total) * 100).toFixed(0) : '0';
  const principalAngle = total > 0 ? (results.principal / total) * 360 : 0;

  return (
    <div style={containerStyle}>
      <header style={headerStyle}>
        <h1 style={h1Style}>🚤 {t("title")}</h1>
        <p style={subtitleStyle}>{t("subtitle")}</p>
      </header>

      <section style={calculatorToolStyle}>
        <div style={toolContainerStyle}>
          <div style={toolHeaderStyle}>
            <h2 style={toolH2Style}>{t("toolTitle")}</h2>
          </div>

          <div style={calculatorLayoutStyle}>
            <div style={calculatorFormStyle}>
              <div style={formTabsStyle}>
                <button
                  type="button"
                  style={tabBtnStyle(currentTab === 'standard')}
                  onClick={() => setCurrentTab('standard')}
                >
                  {t("tabTotalPrice")}
                </button>
                <button
                  type="button"
                  style={tabBtnStyle(currentTab === 'reverse')}
                  onClick={() => setCurrentTab('reverse')}
                >
                  {t("tabMonthlyPayment")}
                </button>
              </div>

              <form onSubmit={(e) => { e.preventDefault(); handleCalculate(); }}>
                {currentTab === 'standard' ? (
                  <div style={formGroupStyle}>
                    <label style={labelStyle} htmlFor="boat-price">{t("boatPrice")}</label>
                    <div style={inputWrapperStyle}>
                      <span style={inputPrefixStyle}>$</span>
                      <input
                        type="number"
                        id="boat-price"
                        style={{ ...inputStyle, paddingLeft: '2rem' }}
                        value={inputs.boatPrice}
                        onChange={(e) => setInputs({ ...inputs, boatPrice: parseFloat(e.target.value) || 0 })}
                        min="0"
                        step="100"
                        required
                      />
                    </div>
                  </div>
                ) : (
                  <div style={formGroupStyle}>
                    <label style={labelStyle} htmlFor="monthly-payment-input">{t("monthlyPaymentInput")}</label>
                    <div style={inputWrapperStyle}>
                      <span style={inputPrefixStyle}>$</span>
                      <input
                        type="number"
                        id="monthly-payment-input"
                        style={{ ...inputStyle, paddingLeft: '2rem' }}
                        value={inputs.monthlyPaymentInput}
                        onChange={(e) => setInputs({ ...inputs, monthlyPaymentInput: parseFloat(e.target.value) || 0 })}
                        min="0"
                        step="10"
                      />
                    </div>
                  </div>
                )}

                <div style={formGroupStyle}>
                  <label style={labelStyle} htmlFor="loan-term">{t("loanTerm")}</label>
                  <div style={inputWrapperStyle}>
                    <input
                      type="number"
                      id="loan-term"
                      style={inputStyle}
                      value={inputs.loanTerm}
                      onChange={(e) => setInputs({ ...inputs, loanTerm: parseFloat(e.target.value) || 0 })}
                      min="1"
                      step="1"
                      required
                    />
                    <select
                      id="loan-term-unit"
                      style={selectStyle}
                      value={inputs.loanTermUnit}
                      onChange={(e) => setInputs({ ...inputs, loanTermUnit: e.target.value as 'years' | 'months' })}
                    >
                      <option value="years">{t("years")}</option>
                      <option value="months">{t("months")}</option>
                    </select>
                  </div>
                </div>

                <div style={formGroupStyle}>
                  <label style={labelStyle} htmlFor="interest-rate">{t("interestRate")}</label>
                  <div style={inputWrapperStyle}>
                    <input
                      type="number"
                      id="interest-rate"
                      style={{ ...inputStyle, paddingRight: '2.5rem' }}
                      value={inputs.interestRate}
                      onChange={(e) => setInputs({ ...inputs, interestRate: parseFloat(e.target.value) || 0 })}
                      min="0"
                      max="30"
                      step="0.1"
                      required
                    />
                    <span style={inputSuffixStyle}>%</span>
                  </div>
                </div>

                <div style={formGroupStyle}>
                  <label style={labelStyle} htmlFor="down-payment">{t("downPayment")}</label>
                  <div style={inputWrapperStyle}>
                    {inputs.downPaymentUnit === 'dollar' && <span style={inputPrefixStyle}>$</span>}
                    <input
                      type="number"
                      id="down-payment"
                      style={{
                        ...inputStyle,
                        paddingLeft: inputs.downPaymentUnit === 'dollar' ? '2rem' : '1rem',
                        paddingRight: inputs.downPaymentUnit === 'percent' ? '2.5rem' : '1rem',
                      }}
                      value={inputs.downPayment}
                      onChange={(e) => setInputs({ ...inputs, downPayment: parseFloat(e.target.value) || 0 })}
                      min="0"
                      step="100"
                      required
                    />
                    {inputs.downPaymentUnit === 'percent' && <span style={inputSuffixStyle}>%</span>}
                    <select
                      id="down-payment-unit"
                      style={selectStyle}
                      value={inputs.downPaymentUnit}
                      onChange={(e) => setInputs({ ...inputs, downPaymentUnit: e.target.value as 'dollar' | 'percent' })}
                    >
                      <option value="dollar">$</option>
                      <option value="percent">%</option>
                    </select>
                  </div>
                </div>

                <div style={formGroupStyle}>
                  <label style={labelStyle} htmlFor="trade-in">{t("tradeIn")}</label>
                  <div style={inputWrapperStyle}>
                    <span style={inputPrefixStyle}>$</span>
                    <input
                      type="number"
                      id="trade-in"
                      style={{ ...inputStyle, paddingLeft: '2rem' }}
                      value={inputs.tradeIn}
                      onChange={(e) => setInputs({ ...inputs, tradeIn: parseFloat(e.target.value) || 0 })}
                      min="0"
                      step="100"
                    />
                  </div>
                </div>

                <div style={formGroupStyle}>
                  <label style={labelStyle} htmlFor="sales-tax">{t("salesTax")}</label>
                  <div style={inputWrapperStyle}>
                    <input
                      type="number"
                      id="sales-tax"
                      style={{ ...inputStyle, paddingRight: '2.5rem' }}
                      value={inputs.salesTax}
                      onChange={(e) => setInputs({ ...inputs, salesTax: parseFloat(e.target.value) || 0 })}
                      min="0"
                      max="20"
                      step="0.1"
                    />
                    <span style={inputSuffixStyle}>%</span>
                  </div>
                </div>

                <div style={formGroupStyle}>
                  <label style={labelStyle} htmlFor="fees">{t("fees")}</label>
                  <div style={inputWrapperStyle}>
                    <span style={inputPrefixStyle}>$</span>
                    <input
                      type="number"
                      id="fees"
                      style={{ ...inputStyle, paddingLeft: '2rem' }}
                      value={inputs.fees}
                      onChange={(e) => setInputs({ ...inputs, fees: parseFloat(e.target.value) || 0 })}
                      min="0"
                      step="100"
                    />
                  </div>
                </div>

                <div style={checkboxGroupStyle}>
                  <input
                    type="checkbox"
                    id="include-fees"
                    style={checkboxStyle}
                    checked={inputs.includeFees}
                    onChange={(e) => setInputs({ ...inputs, includeFees: e.target.checked })}
                  />
                  <label style={checkboxLabelStyle} htmlFor="include-fees">{t("includeFees")}</label>
                </div>

                <div style={buttonGroupStyle}>
                  <button type="submit" style={calculateBtnStyle}>{t("calculateButton")}</button>
                  <button type="button" style={clearBtnStyle} onClick={handleClear}>{t("clearButton")}</button>
                </div>
              </form>
            </div>

            {showResults && (
              <div style={resultsStyle}>
                <h3 style={resultsH3Style}>{t("resultsTitle")}</h3>
                <div style={monthlyPaymentCardStyle}>
                  <div style={monthlyPaymentLabelStyle}>{t("monthlyPaymentLabel")}</div>
                  <div style={monthlyPaymentValueStyle}>${results.monthlyPayment.toFixed(2)}</div>
                </div>

                <div style={resultItemStyle}>
                  <span style={resultLabelStyle}>{t("totalLoanAmount")}</span>
                  <span style={resultValueStyle}>${results.totalLoan.toFixed(2)}</span>
                </div>

                <div style={resultItemStyle}>
                  <span style={resultLabelStyle}>{t("salesTaxAmount")}</span>
                  <span style={resultValueStyle}>${results.salesTaxAmount.toFixed(2)}</span>
                </div>

                <div style={resultItemStyle}>
                  <span style={resultLabelStyle}>{t("upfrontPayment")}</span>
                  <span style={resultValueStyle}>${results.upfrontPayment.toFixed(2)}</span>
                </div>

                <div style={resultItemStyle}>
                  <span style={resultLabelStyle}>{t("totalPayments")}</span>
                  <span style={resultValueStyle}>${results.totalPayments.toFixed(2)}</span>
                </div>

                <div style={resultItemStyle}>
                  <span style={resultLabelStyle}>{t("totalInterest")}</span>
                  <span style={resultValueStyle}>${results.totalInterest.toFixed(2)}</span>
                </div>

                <div style={{ ...resultItemStyle, ...resultHighlightStyle, borderBottom: 'none' }}>
                  <span style={resultLabelStyle}>{t("totalCost")}</span>
                  <span style={resultValueStyle}>${results.totalCost.toFixed(2)}</span>
                </div>

                <div style={chartContainerStyle}>
                  <svg viewBox="0 0 200 200" style={{ width: '200px', height: '200px', margin: '0 auto', display: 'block' }}>
                    <text x="100" y="20" textAnchor="middle" fontSize="14" fontWeight="bold" fill="#1e293b">
                      {t("loanBreakdown")}
                    </text>
                    <path d={getPieChartPath(0, principalAngle)} fill={colors.blue} />
                    <path d={getPieChartPath(principalAngle, 360)} fill={colors.green} />
                    <text x="130" y="120" textAnchor="middle" fontSize="14" fontWeight="bold" fill="white">
                      {principalPercent}%
                    </text>
                    <text x="70" y="100" textAnchor="middle" fontSize="14" fontWeight="bold" fill="white">
                      {interestPercent}%
                    </text>
                  </svg>
                  <div style={chartLegendStyle}>
                    <div style={legendItemStyle}>
                      <div style={{ ...legendColorStyle, background: colors.blue }}></div>
                      <span>{t("principal")}</span>
                    </div>
                    <div style={legendItemStyle}>
                      <div style={{ ...legendColorStyle, background: colors.green }}></div>
                      <span>{t("interest")}</span>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {showAmortization && amortizationSchedule.length > 0 && (
            <div style={amortizationSectionStyle}>
              <h3 style={resultsH3Style}>{t("amortizationTitle")}</h3>
              <table style={tableStyle}>
                <thead>
                  <tr>
                    <th style={thStyle}>{t("month")}</th>
                    <th style={thStyle}>{t("payment")}</th>
                    <th style={thStyle}>{t("principalPayment")}</th>
                    <th style={thStyle}>{t("interestPayment")}</th>
                    <th style={thStyle}>{t("balance")}</th>
                  </tr>
                </thead>
                <tbody>
                  {amortizationSchedule.map((row) => (
                    <tr key={row.month}>
                      <td style={tdStyle}>{row.month}</td>
                      <td style={tdStyle}>${row.payment.toFixed(2)}</td>
                      <td style={tdStyle}>${row.principal.toFixed(2)}</td>
                      <td style={tdStyle}>${row.interest.toFixed(2)}</td>
                      <td style={tdStyle}>${row.balance.toFixed(2)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t("introTitle")}</h2>
        <p style={paragraphStyle}>{t("introParagraph1")}</p>
        <p style={paragraphStyle}>{t("introParagraph2")}</p>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t("howToUseTitle")}</h2>
        <ol style={listStyle}>
          <li style={listItemStyle}>{t("howToStep1")}</li>
          <li style={listItemStyle}>{t("howToStep2")}</li>
          <li style={listItemStyle}>{t("howToStep3")}</li>
          <li style={listItemStyle}>{t("howToStep4")}</li>
          <li style={listItemStyle}>{t("howToStep5")}</li>
          <li style={listItemStyle}>{t("howToStep6")}</li>
        </ol>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t("insightsTitle")}</h2>
        <ul style={listStyle}>
          <li style={listItemStyle}>{t("insight1")}</li>
          <li style={listItemStyle}>{t("insight2")}</li>
          <li style={listItemStyle}>{t("insight3")}</li>
          <li style={listItemStyle}>{t("insight4")}</li>
        </ul>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t("detailsTitle")}</h2>
        <p style={paragraphStyle}>{t("detailsParagraph1")}</p>
        <p style={paragraphStyle}>{t("detailsParagraph2")}</p>
        <p style={paragraphStyle}>{t("detailsParagraph3")}</p>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t("faqTitle")}</h2>
        <div style={{ marginBottom: '1.5rem' }}>
          <h3 style={{ ...h2Style, fontSize: '1.25rem', marginBottom: '0.5rem' }}>{t("faq1Question")}</h3>
          <p style={paragraphStyle}>{t("faq1Answer")}</p>
        </div>
        <div style={{ marginBottom: '1.5rem' }}>
          <h3 style={{ ...h2Style, fontSize: '1.25rem', marginBottom: '0.5rem' }}>{t("faq2Question")}</h3>
          <p style={paragraphStyle}>{t("faq2Answer")}</p>
        </div>
        <div style={{ marginBottom: '1.5rem' }}>
          <h3 style={{ ...h2Style, fontSize: '1.25rem', marginBottom: '0.5rem' }}>{t("faq3Question")}</h3>
          <p style={paragraphStyle}>{t("faq3Answer")}</p>
        </div>
        <div>
          <h3 style={{ ...h2Style, fontSize: '1.25rem', marginBottom: '0.5rem' }}>{t("faq4Question")}</h3>
          <p style={paragraphStyle}>{t("faq4Answer")}</p>
        </div>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t("referencesTitle")}</h2>
        <ul style={listStyle}>
          <li style={listItemStyle}>
            <a href="https://www.bankrate.com/loans/personal-loans/boat-loan-calculator/" target="_blank" rel="noopener noreferrer" style={{ color: colors.blue, textDecoration: 'none' }}>
              {t("reference1")}
            </a>
          </li>
          <li style={listItemStyle}>
            <a href="https://www.boattrader.com/research/2025-boat-loan-rates-drop-to-6-49-making-ownership-more-affordable/" target="_blank" rel="noopener noreferrer" style={{ color: colors.blue, textDecoration: 'none' }}>
              {t("reference2")}
            </a>
          </li>
          <li style={listItemStyle}>
            <a href="https://elitedirectfinance.com/boat-loans/calculator/" target="_blank" rel="noopener noreferrer" style={{ color: colors.blue, textDecoration: 'none' }}>
              {t("reference3")}
            </a>
          </li>
          <li style={listItemStyle}>
            <a href="https://www.navyfederal.org/loans-cards/auto-loans/boat-loans.html" target="_blank" rel="noopener noreferrer" style={{ color: colors.blue, textDecoration: 'none' }}>
              {t("reference4")}
            </a>
          </li>
        </ul>
      </section>
    </div>
  );
}